import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Loading from "@components/shared/Loading";
import { useSubscriber } from "@hooks/query/useSubscriber";
import { type ConfirmData } from "@components/shared/ConfirmationDetail";
import { type Address } from "@services/subscriber";
import { useGetLevel1Resource } from "@hooks/query/useGetLevel1Resource";
import { formatDate } from "@utils/formatDate";
import { formatCurrency } from "@utils/formatCurrency";
import { useGetBillingGroups } from "@hooks/query/useGetBillingGroups";

const initialPrimaryUser = {
  id: "",
  name: "",
};

type User = typeof initialPrimaryUser;

type Subscriber = {
  id: string;
  walletId: string;
  level1Resource: {
    id: string;
    name: string;
    identifier: string;
  };
  accountNumber: string;
  name: string;
  abn?: string;
  acn?: string;
  accountType?: string;
  isActive: boolean;
  billingFrequency: "Q" | "A" | "B";
  billingGroupIds: string[];
  startActiveDate: Date;
  address: Address;
  originalAccountNumber: string;
};

type GetFullAddressType = (address: Address) => string;

type ParseAddressType = (address: Address) => Partial<Address> | undefined;

type ContextValue = {
  subscriber: Subscriber;
  setSubscriber: React.Dispatch<React.SetStateAction<Subscriber>>;
  user: any;
  setUser: React.Dispatch<any>;
  cancel: () => void;
  getFullAddress: GetFullAddressType;
  parseAddress: ParseAddressType;
  getSubscriberInfo: () => ConfirmData;
  getUserInfo: () => ConfirmData;
  getPrimaryUserInfo: () => ConfirmData;

  isSubscriberComplete: boolean;
  setIsSubcriberComplete: React.Dispatch<React.SetStateAction<boolean>>;
  isUserComplete: boolean;
  setIsUserComplete: React.Dispatch<React.SetStateAction<boolean>>;
  workflowInstance: any;
  setWorkflowInstance: React.Dispatch<any>;
  allUsers: any[];
  primaryUser: User;
  setPrimaryUser: React.Dispatch<React.SetStateAction<User>>;
};

const Context = React.createContext<ContextValue | undefined>(undefined);

const initialSubscriber: Subscriber = {
  id: "",
  walletId: "",
  accountNumber: "",
  name: "",
  level1Resource: {
    id: "",
    name: "",
    identifier: "",
  },
  isActive: true,
  billingFrequency: "Q",
  billingGroupIds: [],
  startActiveDate: new Date(),
  address: {
    id: "",
    street: "",
    town: "",
    region: "",
    postcode: "",
    country: "",
    location: "",
  },
  originalAccountNumber: "",
};

function SubscriberCreateOrEditProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subscriberId = searchParams.get("subscriberId") ?? "";
  const level1ResourceId = searchParams.get("level1ResourceId") ?? "";
  const [subscriber, setSubscriber] = React.useState({ ...initialSubscriber });
  const [user, setUser] = React.useState<any>();
  const [isSubscriberComplete, setIsSubcriberComplete] = React.useState(false);
  const [isUserComplete, setIsUserComplete] = React.useState(false);
  const [workflowInstance, setWorkflowInstance] = React.useState<any>();
  const [allUsers, setAllUsers] = React.useState<any[]>([]);
  const [primaryUser, setPrimaryUser] = React.useState({
    ...initialPrimaryUser,
  });

  const cancel = () => {
    Boolean(searchParams.get("newWindow")) ? window.close() : navigate(-1);
  };

  useSubscriber(subscriberId, {
    enabled: Boolean(subscriberId),
    onSuccess: (data: any) => {
      const { address, users = [] } = data;
      setAllUsers(
        // true values first
        users.sort((a: any, b: any) => {
          return a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1;
        }),
      );

      if (primaryUser.id === "") {
        const primaryUser = users.find((i: any) => i.isPrimary);
        setPrimaryUser({
          id: primaryUser?.id ?? "",
          name: primaryUser?.name ?? "",
        });
      }

      if (subscriber.id === "") {
        setSubscriber({
          id: data.id,
          walletId: data.walletId,
          level1Resource: {
            id: data.level1WRS?.id ?? "",
            name: data.level1WRS?.name ?? "",
            identifier: data.level1WRS?.identifier ?? "",
          },
          accountNumber: data.accountNumber ?? "",
          name: data.name ?? "",
          abn: data.abn ?? "",
          acn: data.acn ?? "",
          accountType: data.accountType ?? "",
          isActive: data.isActive,
          billingFrequency: data.billingFrequency ?? "Q",
          billingGroupIds: (data.billingGroups || []).map((bg: any) => bg.id),
          startActiveDate:
            data.startActiveDate && new Date(data.startActiveDate),
          address: {
            id: address?.id ?? "",
            street: address?.street ?? "",
            town: address?.town ?? "",
            region: address?.region ?? "",
            postcode: address?.postcode ?? "",
            country: address?.country ?? "",
            location: address?.location ?? "",
          },
          originalAccountNumber: data.accountNumber,
        });
      }
    },
    keepPreviousData: false,
  });

  useGetLevel1Resource(level1ResourceId, {
    enabled: Boolean(level1ResourceId),
    onSuccess: (data: any) => {
      if (subscriber.level1Resource.id === "") {
        setSubscriber(prev => ({
          ...prev,
          level1Resource: {
            id: data.id,
            name: data.name,
            identifier: data.identifier,
          },
        }));
      }
    },
  });

  const { data: billingGroups = [] } = useGetBillingGroups({
    params: { level1ResourceId: subscriber.level1Resource?.id },
    options: {
      enabled:
        Boolean(subscriber.level1Resource?.id) &&
        (subscriber.billingFrequency === "A" ||
          subscriber.billingFrequency === "B"),
    },
  });

  const getFullAddress: GetFullAddressType = address => {
    const arr = [
      address.street,
      address.town,
      address.region,
      address.postcode,
      address.country,
    ].filter(Boolean);

    return arr.length > 0 ? arr.join(", ") : "-";
  };

  const parseAddress: ParseAddressType = address => {
    const result: Address = { ...address };
    let key: keyof Address;
    for (key in result) {
      if (!result[key]) {
        delete result[key];
      }
    }
    return Object.entries(result).length > 0 ? result : undefined;
  };

  const getAdministrativeCharges = (billingGroup: any) =>
    (billingGroup.waterCharges ?? []).filter(
      (charge: any) => (charge.waterClasses?.length ?? 0) === 0,
    );

  const getDynamicCharges = (billingGroup: any) =>
    (billingGroup.waterCharges ?? []).filter(
      (charge: any) => (charge.waterClasses?.length ?? 0) > 0,
    );

  const getBillingGroupChargeType = (billingGroup: any) => {
    const adminCharges = getAdministrativeCharges(billingGroup);
    const dynamicCharges = getDynamicCharges(billingGroup);

    if (adminCharges.length > 0 && dynamicCharges.length > 0) {
      return `${t("subscriber.administrative")} + ${t("subscriber.dynamic")}`;
    }

    if (adminCharges.length > 0) {
      return t("subscriber.administrative");
    }

    if (dynamicCharges.length > 0) {
      return t("subscriber.dynamic");
    }

    return t("reporting.preflight_reason_no_admin_charge");
  };

  const getAdministrativeChargeDetails = (billingGroup: any) => {
    const adminCharges = getAdministrativeCharges(billingGroup);

    if (adminCharges.length === 0) {
      return null;
    }

    return (
      <ul className="inline-block align-top space-y-1">
        {adminCharges.map((charge: any) => (
          <li key={charge.id}>
            {charge.fixedItemNo ? `${charge.fixedItemNo} - ` : ""}
            {charge.fixedName || "-"} ({formatCurrency(+charge.fixedValue)})
            {charge.effectiveAt
              ? `, ${t("water_charges.field_effective_at")}: ${formatDate(
                  new Date(charge.effectiveAt),
                )}`
              : ""}
          </li>
        ))}
      </ul>
    );
  };

  const getBillingGroupLabel = (billingGroup: any) =>
    `${billingGroup.name} - ${getBillingGroupChargeType(billingGroup)}`;

  const getBillingGroupInfoItems = () => {
    if (
      subscriber.billingFrequency !== "A" &&
      subscriber.billingFrequency !== "B"
    ) {
      return [];
    }

    const selectedBillingGroups = billingGroups.filter((billingGroup: any) =>
      subscriber.billingGroupIds?.includes(billingGroup.id),
    );

    if (selectedBillingGroups.length === 0) {
      return [
        {
          key: t("subscriber.billing_groups"),
          value: "-",
        },
      ];
    }

    return [
      {
        key: t("subscriber.billing_groups"),
        value: (
          <ul className="inline-block align-top space-y-2">
            {selectedBillingGroups.map((billingGroup: any) => (
              <li key={billingGroup.id}>
                <div>{getBillingGroupLabel(billingGroup)}</div>
                {getAdministrativeChargeDetails(billingGroup) && (
                  <div className="text-gray-500">
                    {getAdministrativeChargeDetails(billingGroup)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        ),
      },
    ];
  };

  const getSubscriberInfo = () => {
    return {
      title: t("subscriber.create.subscriber_details"),
      body: [
        {
          key: t("common.level1wrs"),
          value: subscriber.level1Resource.name,
        },
        {
          key: t("subscriber.account_number"),
          value: subscriber.accountNumber,
        },
        {
          key: t("subscriber.name"),
          value: subscriber.name,
        },
        {
          key: t("subscriber.abn"),
          value: subscriber.abn || "-",
        },
        {
          key: t("subscriber.acn"),
          value: subscriber.acn || "-",
        },
        {
          key: t("subscriber.accountType"),
          value: subscriber.accountType || "-",
        },
        {
          key: t("subscriber.create.subscriber_address"),
          value: subscriber.address ? getFullAddress(subscriber.address) : "",
        },
        {
          key: t("subscriber.create.start_active_date"),
          value: subscriber.startActiveDate
            ? formatDate(subscriber.startActiveDate)
            : "-",
        },
        {
          key: t("common.status"),
          value: subscriber.isActive
            ? t("common.active")
            : t("common.inactive"),
        },
        {
          key: t("subscriber.billing_type"),
          value:
            subscriber.billingFrequency === "A"
              ? t("subscriber.administrative")
              : subscriber.billingFrequency === "B"
              ? t("subscriber.both")
              : t("subscriber.dynamic"),
        },
        ...getBillingGroupInfoItems(),
      ],
    };
  };

  const getUserInfo = () => {
    return {
      title: t("user.details"),
      body: [
        {
          key: t("subscriber.name"),
          value: subscriber?.name,
        },
        {
          key: t("user.username"),
          value: user?.name,
        },
        {
          key: t("user.email"),
          value: user?.email,
        },
        {
          key: t("user.tel"),
          value: user?.tel,
        },
      ],
    };
  };

  const getPrimaryUserInfo = () => {
    return {
      title: t("user.create_primary_user.details"),
      body: [
        {
          key: t("user.username"),
          value: primaryUser.name,
        },
      ],
    };
  };

  if (
    (subscriberId && !subscriber.id) ||
    (level1ResourceId && !subscriber.level1Resource?.id)
  ) {
    return (
      <div className="py-20">
        <Loading />
      </div>
    );
  }

  return (
    <Context.Provider
      value={{
        subscriber,
        setSubscriber,
        user,
        setUser,
        cancel,
        getFullAddress,
        parseAddress,
        getSubscriberInfo,
        getUserInfo,
        getPrimaryUserInfo,
        isSubscriberComplete,
        setIsSubcriberComplete,
        isUserComplete,
        setIsUserComplete,
        workflowInstance,
        setWorkflowInstance,
        allUsers,
        primaryUser,
        setPrimaryUser,
      }}
    >
      {children}
    </Context.Provider>
  );
}

function useSubscriberCreateOrEditContext() {
  const context = React.useContext(Context);
  if (context === undefined) {
    throw new Error(
      "useSubscriberCreateOrEditContext must be used within a SubscriberCreateOrEditProvider",
    );
  }
  return context;
}

export { SubscriberCreateOrEditProvider, useSubscriberCreateOrEditContext };
